import type { RelayOptionsConfig } from "@/config/types"
import { normalizeRelayUrl, uniqueRelayUrls } from "@/utils/url"
import { RelayConnection, type RelayConnectionOptions, type WebSocketLike } from "./RelayConnection"
import { SubscriptionManager } from "./SubscriptionManager"
import type {
  NostrEvent,
  NostrFilter,
  PublishSummary,
  RelayDescriptor,
  RelayState,
  RelayStatus,
} from "./types"

export interface PoolSubscription {
  id: string
  relays: string[]
  close: () => void
}

export interface SubscribeOptions {
  relays?: string[]
  onEvent: (event: NostrEvent, relay: string) => void
  onEose?: () => void
  onClosed?: (message: string, relay: string) => void
}

export interface RelayPoolOptions {
  relayOptions: RelayOptionsConfig
  relays?: RelayDescriptor[]
  authenticate?: (challenge: string, relayUrl: string) => Promise<NostrEvent | undefined>
  webSocketFactory?: (url: string) => WebSocketLike
  onNotice?: (message: string) => void
}

/**
 * Owns every relay connection and every subscription.
 *
 * The relay-first UI reads through this pool: it subscribes to an arbitrary set
 * of relays (a relay set, one relay, or the user's write relays) and every event
 * is de-duplicated by event id. Nothing in the UI touches a WebSocket directly.
 */
export class RelayPool {
  private readonly connections = new Map<string, RelayConnection>()
  private readonly descriptors = new Map<string, RelayDescriptor>()
  private readonly subscriptions: SubscriptionManager
  private readonly options: RelayPoolOptions
  private readonly listeners = new Set<() => void>()
  private statusSnapshot: RelayStatus[] = []
  private statusDirty = true

  constructor(options: RelayPoolOptions) {
    this.options = options
    this.subscriptions = new SubscriptionManager({
      eoseTimeout: Math.max(options.relayOptions.eoseTimeout, 1000),
    })
    this.setRelays(options.relays ?? [])
  }

  /** Creates connections for new relays and disconnects removed ones. */
  setRelays(relays: RelayDescriptor[]): void {
    const wanted = new Map<string, RelayDescriptor>()
    for (const relay of relays) {
      const url = normalizeRelayUrl(relay.url)
      if (!url) continue
      wanted.set(url, { ...relay, url })
    }

    for (const [url, connection] of this.connections) {
      const descriptor = wanted.get(url)
      if (!descriptor) {
        connection.disconnect()
        this.connections.delete(url)
        continue
      }
      connection.read = descriptor.read
      connection.write = descriptor.write
    }

    for (const [url, descriptor] of wanted) {
      let connection = this.connections.get(url)
      if (!connection) {
        connection = new RelayConnection(url, this.connectionOptions(descriptor))
        this.connections.set(url, connection)
      }
      connection.connect()
    }

    this.descriptors.clear()
    for (const [url, descriptor] of wanted) this.descriptors.set(url, descriptor)
    this.markDirty()
  }

  private connectionOptions(descriptor: RelayDescriptor): RelayConnectionOptions {
    const relayOptions = this.options.relayOptions
    return {
      read: descriptor.read,
      write: descriptor.write,
      connectTimeout: relayOptions.connectTimeout,
      publishTimeout: relayOptions.publishTimeout,
      maxReconnectAttempts: relayOptions.maxReconnectAttempts,
      reconnectBaseDelay: relayOptions.reconnectBaseDelay,
      verifyEvents: relayOptions.verifyEvents,
      authenticate: this.options.authenticate,
      webSocketFactory: this.options.webSocketFactory,
      onStateChange: () => this.markDirty(),
      onNotice: (message, connection) => this.options.onNotice?.(`${connection.url}: ${message}`),
    }
  }

  /** Opens a connection on demand (relay detail pages, relay reviews, hints). */
  ensureRelay(url: string): RelayConnection | undefined {
    const normalized = normalizeRelayUrl(url)
    if (!normalized) return undefined
    let connection = this.connections.get(normalized)
    if (!connection) {
      const descriptor: RelayDescriptor = { url: normalized, read: true, write: true, priority: 900 }
      connection = new RelayConnection(normalized, this.connectionOptions(descriptor))
      this.connections.set(normalized, connection)
      this.descriptors.set(normalized, descriptor)
      this.markDirty()
    }
    connection.connect()
    return connection
  }

  readRelays(): string[] {
    return [...this.descriptors.values()].filter((relay) => relay.read).map((relay) => relay.url)
  }

  writeRelays(): string[] {
    return [...this.descriptors.values()].filter((relay) => relay.write).map((relay) => relay.url)
  }

  getRelay(url: string): RelayConnection | undefined {
    const normalized = normalizeRelayUrl(url)
    return normalized ? this.connections.get(normalized) : undefined
  }

  stateOf(url: string): RelayState {
    const normalized = normalizeRelayUrl(url)
    if (!normalized) return "error"
    return this.connections.get(normalized)?.state ?? "idle"
  }

  // ------------------------------------------------------------ subscriptions

  subscribe(filters: NostrFilter | NostrFilter[], options: SubscribeOptions): PoolSubscription {
    const filterList = Array.isArray(filters) ? filters : [filters]
    const relays = uniqueRelayUrls(
      options.relays && options.relays.length > 0 ? options.relays : this.readRelays(),
    )

    if (relays.length === 0) {
      options.onEose?.()
      return { id: `empty-${Date.now().toString(36)}`, relays: [], close: () => undefined }
    }

    const subscription = this.subscriptions.create(
      filterList,
      {
        onEvent: options.onEvent,
        onEose: options.onEose,
        onClosed: options.onClosed,
      },
      relays,
    )

    for (const url of relays) {
      const connection = this.ensureRelay(url)
      if (!connection) continue
      connection.subscribe(subscription.id, filterList, {
        onEvent: (event) => {
          this.subscriptions.receive(subscription.id, url, event)
        },
        onEose: () => {
          this.subscriptions.eose(subscription.id, url)
        },
        onClosed: (message) => {
          this.subscriptions.closed(subscription.id, url, message)
        },
      })
    }

    return {
      id: subscription.id,
      relays,
      close: () => {
        for (const url of relays) this.connections.get(url)?.unsubscribe(subscription.id)
        this.subscriptions.close(subscription.id)
      },
    }
  }

  // ------------------------------------------------------------------ publish

  /** Publishes a signed event and aggregates the relays' OK responses. */
  async publish(event: NostrEvent, relays?: string[]): Promise<PublishSummary> {
    const targets = uniqueRelayUrls(relays && relays.length > 0 ? relays : this.writeRelays())
    const results: PublishSummary["results"] = []

    await Promise.all(
      targets.map(async (url) => {
        const connection = this.ensureRelay(url)
        if (!connection) {
          results.push({ relay: url, ok: false, message: "invalid relay URL" })
          return
        }
        if (!connection.write) {
          results.push({ relay: url, ok: false, message: "relay is configured read-only" })
          return
        }
        const result = await connection.publish(event)
        results.push({ relay: url, ok: result.ok, message: result.message })
      }),
    )

    const accepted = results.filter((result) => result.ok).length
    const timedOut = results.filter((result) => /timed out/i.test(result.message)).length
    return {
      event,
      results,
      accepted,
      rejected: results.length - accepted,
      timedOut,
    }
  }

  // ------------------------------------------------------------------- status

  /** Stable snapshot for `useSyncExternalStore`. */
  getStatusSnapshot = (): RelayStatus[] => {
    if (this.statusDirty) {
      this.statusSnapshot = [...this.connections.values()]
        .map((connection) => ({
          url: connection.url,
          state: connection.state,
          read: connection.read,
          write: connection.write,
          latency: connection.latency,
          eventsReceived: connection.eventsReceived,
          lastError: connection.lastError,
          lastConnectedAt: connection.lastConnectedAt,
        }))
        .sort((a, b) => a.url.localeCompare(b.url))
      this.statusDirty = false
    }
    return this.statusSnapshot
  }

  subscribeToStatus = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private markDirty(): void {
    this.statusDirty = true
    for (const listener of this.listeners) {
      try {
        listener()
      } catch (error) {
        console.error("[relay-pool] status listener failed", error)
      }
    }
  }

  closeAll(): void {
    this.subscriptions.closeAll()
    for (const connection of this.connections.values()) connection.disconnect()
    this.connections.clear()
    this.descriptors.clear()
    this.markDirty()
  }
}

